import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Box, Column, Divider, InputSelect, Row } from '~/commonComponents';
import { AddMoreOnsiteInterviewButton } from '~/components/PermanentJobsV2/components';
import { JobPostingStatus } from '~/domain/PermanentJobs';
import { WarningSolidIcon } from '~/images/icons';
import { firePermJobOverviewAddInterviewsClicked } from '~/posthogs/events';
import { ClosedBanner } from './ClosedBanner';
import { JobPostingStatusOptions } from './Constants';
import { JobStatusInfo } from './JobStatusInfo';
import { SuspendedBanner } from './SuspendedBanner';

interface IJobOverviewHeaderProps {
  id: string;
  officeName: string;
  professional: string;
  status: JobPostingStatus;
  title: string;
  closedAt?: Date;
  hasScheduledOnsiteInterviews?: boolean;
  postedAt?: Date;
  suspendedAt?: Date;
  officeId?: string;
  officePlanId?: string;
  onCloseJob?(): void;
}

export const JobOverviewHeader = ({
  closedAt,
  hasScheduledOnsiteInterviews = false,
  id,
  officeName,
  postedAt,
  professional,
  status,
  suspendedAt,
  officeId,
  officePlanId,
  title,
  onCloseJob,
}: IJobOverviewHeaderProps) => {
  const [jobStatus, setJobStatus] = useState<JobPostingStatus>(status);

  const isPessimisticActive = status === JobPostingStatus.Active;
  const isPessimisticClosed = status === JobPostingStatus.Closed;
  const isPessimisticSuspended = status === JobPostingStatus.Suspended;

  const isClosed = isPessimisticClosed && closedAt;
  const isSuspended = isPessimisticSuspended && suspendedAt;
  const showCloseOrSuspendDate = isClosed || isSuspended;

  const handleJobStatusChanges = useCallback(
    (newStatus: string) => {
      const status = newStatus as JobPostingStatus;

      if (status === JobPostingStatus.Closed) {
        onCloseJob?.();

        return;
      }

      // Currently, only "Closed" status is supported, not possible to update to other statuses
      setJobStatus(status);
    },
    [onCloseJob],
  );

  return (
    <Column marginInline={115} marginTop={32}>
      <Row justifyContent="space-between">
        <Column>
          <Row alignItems="center" gap={6}>
            <Typography as="span" bold size="heading_4">
              {title}
            </Typography>
            {isPessimisticSuspended && <WarningSolidIcon color={colors.warning[500]} size={24} />}
          </Row>
          <Typography as="span">
            <Typography as="span" bold>Professional Type</Typography>: {professional}
          </Typography>
          <Typography as="span">
            <Typography as="span" bold>Office</Typography>: {officeName}
          </Typography>
          <Row alignItems="center" gap={14}>
            <Typography as="span">
              <Typography as="span" bold>Posted on</Typography>: {postedAt ? format(postedAt, 'MMM dd, yyyy') : ''}
            </Typography>
            {showCloseOrSuspendDate && (
              <>
                <Divider isRounded height="100%" marginVertical={0} width={2} />
                {isClosed && (
                  <Typography as="span">
                    <Typography as="span" bold>Closed on</Typography>: {format(closedAt, 'MMM dd, yyyy')}
                  </Typography>
                )}
                {isSuspended && (
                  <Typography as="span">
                    <Typography as="span" bold>Suspended on</Typography>: {format(suspendedAt, 'MMM dd, yyyy')}
                  </Typography>
                )}
              </>
            )}
          </Row>
        </Column>
        <Column alignItems="flex-end" justifyContent="space-between">
          {isPessimisticActive ? (
            <Row alignItems="center" gap={6}>
              <Typography as="span" bold size="body_large" style={{ textWrap: 'nowrap' }}>
                Job Status<Typography as="span">:</Typography>
              </Typography>
              <Box width={180}>
                <InputSelect
                  dataCy="job-status-select"
                  isSelectVisible={false}
                  options={JobPostingStatusOptions}
                  value={jobStatus}
                  setValue={handleJobStatusChanges}
                />
              </Box>
            </Row>
          ) : (
            <JobStatusInfo jobStatus={status} />
          )}
          {isPessimisticActive && (
            <AddMoreOnsiteInterviewButton
              jobPostingId={id}
              variant="lite"
              onTrack={() => firePermJobOverviewAddInterviewsClicked({ postingId: id })}
            />
          )}
        </Column>
      </Row>

      <ClosedBanner
        hasScheduledOnsiteInterviews={hasScheduledOnsiteInterviews}
        jobStatus={status}
      />
      <SuspendedBanner jobStatus={status} officeId={officeId} officePlanId={officePlanId} />

      <Box paddingInline={36} marginBottom={6}>
        <Divider marginVertical={22} />
      </Box>
    </Column>
  );
};
